import { useEffect, useState } from 'react';
import { useMarkupStore } from '../store/useMarkupStore';
import { onAuthChange, logOut } from '../services/auth';
import { subscribeFeedback } from '../services/firestore';
import { AccessDeniedError, loadOrCreateUserProfile } from '../services/userProfile';
import { parseArchiveBefore } from '../utils/helpers';
import { log } from '../utils/logger';
import type { UserInfo } from '../types';

export const useFirebaseSync = () => {
  const config = useMarkupStore((state) => state.config);
  const setCurrentUser = useMarkupStore((state) => state.setCurrentUser);
  const setIsAuthenticated = useMarkupStore((state) => state.setIsAuthenticated);
  const setFeedbackItems = useMarkupStore((state) => state.setFeedbackItems);
  const setAuthNotice = useMarkupStore((state) => state.setAuthNotice);

  // Uid of a user who is signed in *and* admitted (has a profile). This is
  // deliberately NOT read from the store: `isAuthenticated`/`currentUser` are
  // persisted to localStorage, so on a page refresh they rehydrate as
  // already-signed-in while Firebase is still restoring the session from
  // IndexedDB. Gating the feedback subscription on the persisted flag meant it
  // never changed value after auth resolved, so the effect never re-ran and no
  // feedback was ever queried. This piece of state starts null on every load
  // and only flips when Firebase actually reports a user, which is the signal
  // the subscription needs.
  const [authUid, setAuthUid] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthChange(async (firebaseUser) => {
      log('Auth state changed:', firebaseUser ? 'signed in' : 'signed out');

      if (!firebaseUser) {
        setAuthUid(null);
        setCurrentUser(null);
        setIsAuthenticated(false);
        // Don't leave the last user's tickets (and their pins) on the page
        // for whoever uses this browser next.
        setFeedbackItems([]);
        return;
      }

      try {
        const profile = await loadOrCreateUserProfile(
          firebaseUser.uid,
          firebaseUser.email || '',
          firebaseUser.emailVerified,
          firebaseUser.displayName || undefined
        );
        const user: UserInfo = {
          id: profile.id,
          email: profile.email,
          name: profile.name,
          role: profile.role,
          avatar: profile.avatar,
        };
        setAuthNotice(null);
        setCurrentUser(user);
        setIsAuthenticated(true);
        setAuthUid(firebaseUser.uid);
      } catch (error) {
        // No profile means no access — the rules would refuse every read
        // anyway. Sign back out so the form can say why, rather than showing
        // an empty, broken inbox. (This used to fall back to a made-up
        // "basic" user and carry on.)
        setAuthNotice(
          error instanceof AccessDeniedError
            ? error.message
            : 'We couldn’t load your account. Please try again.'
        );
        if (!(error instanceof AccessDeniedError)) {
          console.error('[markup] Failed to load user profile');
        }
        setAuthUid(null);
        setCurrentUser(null);
        setIsAuthenticated(false);
        // A company account waiting on verification stays signed in so
        // "resend verification" works; anything else is signed out.
        if (!(error instanceof AccessDeniedError && error.reason === 'unverified')) {
          await logOut().catch(() => {});
        }
      }
    });

    return () => unsubscribeAuth();
  }, [setCurrentUser, setIsAuthenticated, setAuthNotice, setFeedbackItems]);

  // Cutoff for config.archiveBefore. Kept as a timestamp (not a Date) so the
  // effect below re-runs when the configured date changes, but not on every
  // render that hands us an equivalent value.
  const archiveBeforeMs = parseArchiveBefore(config.archiveBefore)?.getTime() ?? null;

  useEffect(() => {
    if (!config.projectId || !authUid) return;

    return subscribeFeedback(config.projectId, (feedbackItems) => {
      // Apply the archiveBefore cutoff before anything reaches the store, so
      // older tickets are absent from the list and the on-page pins alike.
      // Nothing is written — clearing the config prop brings them back.
      const visibleItems =
        archiveBeforeMs === null
          ? feedbackItems
          : feedbackItems.filter(
              (item) => new Date(item.createdAt).getTime() >= archiveBeforeMs
            );
      setFeedbackItems(visibleItems);
    });
  }, [config.projectId, setFeedbackItems, authUid, archiveBeforeMs]);
};
